import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onToggle
}: FAQItemProps) {
  return <div className="bg-surface border border-white/5 rounded-xl overflow-hidden" data-id="element-88">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-5 md:p-6 text-left cursor-pointer group" aria-expanded={isOpen} data-id="element-89">
        <span className="text-white font-semibold text-sm md:text-base pr-4 leading-snug" data-id="element-90">
          {question}
        </span>
        <span className="text-gold shrink-0" data-id="element-91">
          {isOpen ? <MinusIcon className="w-5 h-5" data-id="element-92" /> : <PlusIcon className="w-5 h-5" data-id="element-93" />}
        </span>
      </button>
      <AnimatePresence initial={false} data-id="element-94">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-95">
            <div className="px-5 md:px-6 pb-5 md:pb-6" data-id="element-96">
              <p className="text-gray-400 text-sm md:text-base leading-relaxed" data-id="element-97">
                {answer}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section className="py-14 md:py-24 px-5 md:px-8 bg-[#0a0a0a]" data-id="element-98">
      <div className="max-w-3xl mx-auto" data-id="element-99">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUpVariants} className="text-center mb-10 md:mb-12" data-id="element-100">
          <h2 className="text-3xl md:text-4xl font-bold text-gold" data-id="element-101">
            Preguntas Frecuentes
          </h2>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={staggerContainer} className="space-y-3" data-id="element-102">
          <motion.div variants={fadeUpVariants} data-id="element-103">
            <FAQItem question="¿Cuánto tiempo toma ver resultados?" answer="La mayoría de nuestros clientes ven un aumento en citas confirmadas dentro de las primeras 2-3 semanas. El sistema se optimiza continuamente, así que los resultados mejoran cada mes." isOpen={openIndex === 0} onToggle={() => toggle(0)} data-id="element-104" />
          </motion.div>
          <motion.div variants={fadeUpVariants} data-id="element-105">
            <FAQItem question="¿Funciona para salones pequeños?" answer="Absolutamente. Nuestro sistema está diseñado para salones de todos los tamaños. De hecho, los salones pequeños suelen ver el impacto más rápido porque cada cliente nuevo representa un porcentaje mayor de sus ingresos." isOpen={openIndex === 1} onToggle={() => toggle(1)} data-id="element-106" />
          </motion.div>
          <motion.div variants={fadeUpVariants} data-id="element-107">
            <FAQItem question="¿Necesito tener redes sociales activas?" answer="No necesariamente. Aunque las redes sociales ayudan, nuestro sistema de atracción funciona principalmente a través de búsquedas directas — personas que ya están buscando servicios como los tuyos en tu zona." isOpen={openIndex === 2} onToggle={() => toggle(2)} data-id="element-108" />
          </motion.div>
          <motion.div variants={fadeUpVariants} data-id="element-109">
            <FAQItem question="¿Qué pasa si ya he probado publicidad y no funcionó?" answer="La diferencia está en el sistema completo. La mayoría de agencias solo hacen anuncios. Nosotros combinamos atracción + conversión + retención. Si solo haces anuncios sin un sistema de conversión y retención, estás tirando dinero." isOpen={openIndex === 3} onToggle={() => toggle(3)} data-id="element-110" />
          </motion.div>
          <motion.div variants={fadeUpVariants} data-id="element-111">
            <FAQItem question="¿Cuánto cuesta el servicio?" answer="Cada plan se personaliza según el tamaño de tu salón, tu zona y tus objetivos. Agenda tu diagnóstico gratuito y te presentaremos opciones que se ajusten a tu presupuesto. Recuerda: si en 30 días no ves más citas, no pagas." isOpen={openIndex === 4} onToggle={() => toggle(4)} data-id="element-112" />
          </motion.div>
          <motion.div variants={fadeUpVariants} data-id="element-113">
            <FAQItem question="¿Trabajan con salones en mi ciudad?" answer="Trabajamos con salones en todo Estados Unidos. Sin embargo, solo aceptamos un salón por zona geográfica para evitar conflictos de competencia. Consulta disponibilidad en tu área." isOpen={openIndex === 5} onToggle={() => toggle(5)} data-id="element-114" />
          </motion.div>
        </motion.div>
      </div>
    </section>;
}